import { BrowserRouter, Route, Routes, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HelmetProvider } from "react-helmet-async";

import Header from "./Header";
import PracticeLayout from "./PracticeLayout";
import AccountSetup from "./Pages/AccountSetup";
import SignIn from "./Pages/SignIn";
import SignInWithToken from "./Pages/SignInWithToken";
import PasswordReset from "./Pages/PasswordReset";
import PracticeHome from "./Pages/PracticeHome";
import PracticeCase from "./Pages/PracticeCase";
import PracticeAdmin from "./Pages/PracticeAdmin";
import PracticeIntakeForm from "./Pages/PracticeIntakeForm";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import Terms from "./Pages/Terms";
import CustomerFollowup from "./Pages/CustomerFollowup";
import CustomerIntakeForm from "./Pages/CustomerIntakeForm";
import PrivateRoute from "./PrivateRoute";
import { useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, getPractices } from "./services/firebase";
import { actions, useAppState } from "./context/AppContext";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5 } },
});

export { queryClient };

function App() {
  const { dispatch } = useAppState();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          user.practices = await getPractices(user);
          dispatch(actions.setUser(user));
        } else {
          dispatch(actions.setUser(null));
        }
        dispatch(actions.setAuthChecked(true));
      } catch (error) {
        console.log("Auth state change error:", error);
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <BrowserRouter>
          <Routes>
            <Route element={<PracticeLayout />}>
              <Route index element={<SignIn />} />
              <Route
                path="sign-in-with-token/:token"
                element={<SignInWithToken />}
              />
              <Route path="account-setup/:userId" element={<AccountSetup />} />
              <Route path="password-reset" element={<PasswordReset />} />
              <Route path="/case/:caseId" element={<CustomerFollowup />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<SignIn />} />
            </Route>
            <Route path=":slug" element={<PracticeLayout />}>
              <Route
                index
                element={
                  <PrivateRoute>
                    <PracticeHome />
                  </PrivateRoute>
                }
              />
              <Route
                path="new-case"
                element={
                  <PrivateRoute>
                    <PracticeIntakeForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="draft/:draftId"
                element={
                  <PrivateRoute>
                    <PracticeIntakeForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="case/:caseId"
                element={
                  <PrivateRoute>
                    <PracticeCase />
                  </PrivateRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <PrivateRoute>
                    <PracticeAdmin />
                  </PrivateRoute>
                }
              />
              <Route path="customer-form" element={<CustomerIntakeForm />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
